import type { PageParamT } from '@mathflat/shared/@types/mathflatApi'
import { makeAutoObservable, runInAction } from 'mobx'

import type { ReportRes } from '~/@common/api/parentApi'
import { parentApi } from '~/@common/api/parentApi'

// 학부모 보고서 목록 서비스 — 무한스크롤 페이지네이션
export class ParentReportService {
  private _reports: ReportRes[] = []
  private _isLoading = false
  private _isFetchingNextPage = false
  private _pageParam: PageParamT = { page: 0, isLast: false }
  private _studentId: string | null = null
  private _abortController: AbortController | null = null

  constructor() {
    makeAutoObservable(this)
  }

  get reports() {
    return this._reports
  }

  get isLoading() {
    return this._isLoading
  }

  get isFetchingNextPage() {
    return this._isFetchingNextPage
  }

  get hasNextPage() {
    return !this._pageParam.isLast
  }

  get isEmpty() {
    return !this._isLoading && this._reports.length === 0
  }

  // 초기 로드 — studentId 변경 시 호출 (이전 요청은 abort)
  async fetchReports(studentId: string) {
    this._abortController?.abort()
    const controller = new AbortController()
    this._abortController = controller

    this._studentId = studentId
    this._isLoading = true
    this._isFetchingNextPage = false
    this._reports = []
    this._pageParam = { page: 0, isLast: false }

    try {
      const res = await parentApi.getReports(studentId, 0, controller.signal)
      runInAction(() => {
        if (controller.signal.aborted) return
        this._reports = res.content
        this._pageParam.isLast = res.last
      })
    } catch (error) {
      if (controller.signal.aborted) return
      throw error
    } finally {
      runInAction(() => {
        if (controller.signal.aborted) return
        this._isLoading = false
      })
    }
  }

  // 다음 페이지 로드 — 센티넬 진입 시 호출
  async fetchNextPage() {
    if (this._isLoading || this._isFetchingNextPage || this._pageParam.isLast || !this._studentId)
      return

    const controller = this._abortController
    this._isFetchingNextPage = true
    const nextPage = (this._pageParam.page ?? 0) + 1

    try {
      const res = await parentApi.getReports(this._studentId, nextPage, controller?.signal)
      runInAction(() => {
        if (controller?.signal.aborted) return
        this._reports.push(...res.content)
        this._pageParam.page = nextPage
        this._pageParam.isLast = res.last
      })
    } catch (error) {
      if (controller?.signal.aborted) return
      throw error
    } finally {
      runInAction(() => {
        if (controller?.signal.aborted) return
        this._isFetchingNextPage = false
      })
    }
  }
}
